/**
 * Drag-to-reorder wrappers for the category boards and their tiles.
 *
 * dnd-kit rather than the HTML5 drag API, for the same reason the item list
 * uses it: HTML5 drag does not fire on touch at all, and it brings keyboard
 * reordering, which is otherwise the one action here with no keyboard path.
 *
 * Dragging is always started from an explicit handle, never the whole element.
 * A tile is full of links, inline-editable text and buttons; making the card
 * itself draggable turns every mis-aimed click into a drag.
 */

import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { CSSProperties, ReactNode } from 'react'

/** Props a sortable child needs to render its own handle. */
export interface DragHandleProps {
  setNodeRef: (node: HTMLElement | null) => void
  style: CSSProperties
  isDragging: boolean
  /** Spread onto whatever element should start the drag. */
  handleProps: Record<string, unknown>
}

function Sortable({
  id,
  children,
}: {
  id: number
  children: (props: DragHandleProps) => ReactNode
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  })

  return (
    <>
      {children({
        setNodeRef,
        style: {
          transform: CSS.Transform.toString(transform),
          transition,
          // Lifted above its neighbours while moving, or the surrounding cards
          // paint over it mid-drag.
          zIndex: isDragging ? 20 : undefined,
          position: isDragging ? 'relative' : undefined,
          opacity: isDragging ? 0.9 : undefined,
        },
        isDragging,
        handleProps: { ...attributes, ...listeners },
      })}
    </>
  )
}

function useDragSensors() {
  return useSensors(
    // 8px before a drag starts, so clicking a handle-adjacent control still
    // registers as a click.
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    // A short hold on touch, so scrolling the page does not pick a card up.
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
}

interface SortableListProps<T> {
  items: T[]
  getId: (item: T) => number
  onReorder: (orderedIds: number[]) => void
  /** Tiles sit in a grid, categories in a column. */
  layout: 'grid' | 'column'
  className?: string
  children: (item: T, drag: DragHandleProps) => ReactNode
}

export function SortableList<T>({
  items,
  getId,
  onReorder,
  layout,
  className,
  children,
}: SortableListProps<T>) {
  const sensors = useDragSensors()
  const ids = items.map(getId)

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const from = ids.indexOf(Number(active.id))
    const to = ids.indexOf(Number(over.id))
    if (from === -1 || to === -1) return
    // The whole order is sent, not the moved item: positions only mean
    // anything relative to each other, so renumbering the set keeps them
    // dense and makes a dropped request impossible to half-apply.
    onReorder(arrayMove(ids, from, to))
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext
        items={ids}
        strategy={layout === 'grid' ? rectSortingStrategy : verticalListSortingStrategy}
      >
        <div className={className}>
          {items.map((item) => (
            <Sortable key={getId(item)} id={getId(item)}>
              {(drag) => children(item, drag)}
            </Sortable>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )
}

/** The grip itself. Colour is inherited so it works on any coloured band. */
export function DragHandle({
  handleProps,
  label,
  className = '',
}: {
  handleProps: Record<string, unknown>
  label: string
  className?: string
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      // touch-none stops the browser claiming the gesture for scrolling before
      // dnd-kit sees it; without it, dragging on a phone just scrolls.
      className={`shrink-0 cursor-grab touch-none rounded-sm px-1 leading-none opacity-60 transition-opacity hover:opacity-100 active:cursor-grabbing ${className}`}
      {...handleProps}
    >
      <span aria-hidden className="text-lg">
        &#8942;&#8942;
      </span>
    </button>
  )
}
